/**
 * The creator's payouts (specification §5.7, §10): every settled campaign's payout to the
 * signed-in wallet, from every escrow deployment. A payout never expires. The creator claims it
 * from their wallet, paying the gas, or asks Morcast to submit the claim, when Morcast offers it.
 * Either way the escrow pays the wallet the payout names. The claim's data can be downloaded, to
 * claim without Morcast at all. A claim from the wallet needs it connected, which a creator signed
 * in earlier may have to do again. One claim of a payout runs at a time.
 */

import { Download, Wallet } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useConnection } from "wagmi";

import { campaignPath } from "../campaigns/campaigns.js";
import { TransactionLink } from "../components/ChainLinks.js";
import { RequireWallet } from "../components/RequireWallet.js";
import { SignInDialog } from "../components/SignInDialog.js";
import { SupportNote } from "../components/SupportNote.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Loading,
  Notice,
  PageHeader,
  Stat,
  styles,
} from "../components/ui.js";
import { formatAmount, formatUtc } from "../format.js";
import {
  formatTotal,
  isRelaying,
  type Payout,
  useClaim,
  usePayouts,
  useRelayClaim,
} from "../payouts/payouts.js";
import { describeEscrowError } from "../wallet/escrow.js";

export function Payouts() {
  return (
    <RequireWallet reason="Sign in with the wallet you took part with to see your payouts.">
      <PayoutsPage />
    </RequireWallet>
  );
}

function PayoutsPage() {
  const payouts = usePayouts();
  const all = payouts.data?.payouts ?? [];
  const unclaimed = all.filter((payout) => payout.claimed === null);
  const claimed = all.filter((payout) => payout.claimed !== null);

  return (
    <div className={styles.narrowPage}>
      <PageHeader eyebrow="Creator" title="Your payouts">
        Your share of each settled campaign, held by the escrow until you claim it. Payouts never
        expire, and the escrow pays only your wallet, whoever submits the claim.
      </PageHeader>
      {all.length > 0 && (
        <dl className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="To claim" value={formatTotal(unclaimed)} />
          <Stat label="Already claimed" value={formatTotal(claimed)} />
          <Stat label="Settled campaigns" value={all.length} />
        </dl>
      )}
      <div className="mt-8">
        {payouts.isPending && <Loading />}
        {payouts.isError && <ErrorMessage error={payouts.error} />}
        {payouts.data?.payouts.length === 0 && (
          <EmptyState
            icon={Wallet}
            title="No payout yet"
            action={
              <Link to="/campaigns" className={styles.primaryButton}>
                Browse campaigns
              </Link>
            }
          >
            A campaign's payouts appear here once it is settled, a few days after it ends.
          </EmptyState>
        )}
        {payouts.data && payouts.data.payouts.length > 0 && (
          <ul className="space-y-4">
            {payouts.data.payouts.map((payout) => (
              <PayoutCard
                key={`${payout.escrow}-${payout.campaignId}`}
                payout={payout}
                relayClaims={payouts.data.relayClaims}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PayoutCard({ payout, relayClaims }: { payout: Payout; relayClaims: boolean }) {
  const claim = useClaim();
  const relay = useRelayClaim();
  const { isConnected } = useConnection();
  const [signingIn, setSigningIn] = useState(false);
  const claimedTx = payout.claimed?.tx ?? claim.data ?? null;
  const relaying = isRelaying(payout);
  // One claim at a time: from the wallet, or by Morcast.
  const busy = claim.isPending || relay.isPending;

  return (
    <li className={`${styles.card} text-sm`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">
          <Link
            to={campaignPath({ escrow: payout.escrow, id: payout.campaignId })}
            className="hover:underline"
          >
            {payout.title}
          </Link>
        </h2>
        <span className="text-lg font-semibold">{formatAmount(payout.amount, payout.token)}</span>
      </div>

      {claimedTx ? (
        <p className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone="green">Claimed</Badge>
          {payout.claimed && <span>{formatUtc(payout.claimed.at)}</span>}
          <TransactionLink chainId={payout.chainId} hash={claimedTx} />
        </p>
      ) : relaying ? (
        <p className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone="blue">Morcast is claiming it</Badge>
          {payout.relay?.txHash && (
            <TransactionLink chainId={payout.chainId} hash={payout.relay.txHash} />
          )}
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => (isConnected ? claim.mutate(payout) : setSigningIn(true))}
              className={styles.primaryButton}
            >
              {claim.isPending ? "Claiming…" : "Claim"}
            </button>
            {relayClaims && (
              <button
                type="button"
                disabled={busy}
                onClick={() => relay.mutate(payout)}
                className={styles.secondaryButton}
              >
                {relay.isPending ? "Asking Morcast…" : "Claim without gas"}
              </button>
            )}
          </div>
          {!isConnected && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Your wallet is not connected, so claiming from it signs you in with it again first
              {relayClaims ? ". Claiming without gas needs no wallet." : "."}
            </p>
          )}
          <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
        </>
      )}
      {payout.relay?.status === "failed" && !claimedTx && (
        <Notice tone="warning" className="mt-3">
          Morcast could not submit this claim. Claim it from your wallet, or ask Morcast to try
          again.
        </Notice>
      )}
      {claim.isError && (
        <>
          <Notice tone="error" className="mt-3">
            {describeEscrowError(claim.error)}
          </Notice>
          <SupportNote className="mt-2" />
        </>
      )}
      {relay.isError && <ErrorMessage error={relay.error} />}
      <p className="mt-4 flex items-start gap-1.5 border-t border-zinc-200 pt-3 text-xs text-zinc-500 dark:border-white/10 dark:text-zinc-400">
        <Download aria-hidden className="mt-px size-3.5 shrink-0" />
        <span>
          <a
            href={`/api/campaigns/${payout.escrow}/${payout.campaignId}/claims/${payout.wallet}`}
            download={`morcast-claim-${payout.campaignId}.json`}
            className="font-medium underline"
          >
            Download the claim
          </a>{" "}
          to submit it yourself with any tool. It holds the escrow, campaign ID, amount and proof.
        </span>
      </p>
    </li>
  );
}
